import LabPortal from './views/campaigns/LabPortal';
import PlaybookRunPage from './views/campaigns/PlaybookRunPage';
import RouteBoundary from "./RouteBoundary";
import { lazy, Suspense, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import SignIn from "./SignIn";
import AcceptInvitation from "./AcceptInvitation";
import {
  api,
  clearSession,
  listMyTenants,
  loadSession,
  logout,
  saveSession,
  setAuthLostHandler,
  setWorkspaceSession,
  type Workspace,
  type Session,
  type TenantAccess,
} from "./api/client";

import { routeFeatures } from "./api/features";

const Diagnostics = lazy(() => import("./views/Diagnostics"));
const Investigate = lazy(() => import("./views/Investigate"));
const Overview = lazy(() => import("./views/Overview"));
const RemoteSession = lazy(() => import("./views/RemoteSession"));
const RemoteSessionTab = lazy(() => import("./views/RemoteSessionTab"));
const Access = lazy(() => import("./views/Access"));
const Security = lazy(() => import("./views/Security"));
const DeviceDetail = lazy(() => import("./views/DeviceDetail"));
const Devices = lazy(() => import("./views/Devices"));
const RefreshWorkflow = lazy(() => import("./views/devices/RefreshWorkflow"));
const RefreshList = lazy(() => import("./views/devices/RefreshList"));
const RefreshDevice = lazy(() => import("./views/devices/RefreshDevice"));
const HardwareEntry = lazy(() => import("./views/devices/HardwareEntry"));
const LifecycleFeeds = lazy(() => import("./views/devices/LifecycleFeeds"));
const Agents = lazy(() => import("./views/Agents"));
const AgentDetail = lazy(() => import("./views/AgentDetail"));
const AgentConfig = lazy(() => import("./views/AgentConfig"));
const Tests = lazy(() => import("./views/Tests"));
const TenantSettings = lazy(() => import("./views/TenantSettings"));
const Sessions = lazy(() => import("./views/Sessions"));
const Campaigns = lazy(() => import("./views/campaigns/Campaigns"));
const CampaignPage = lazy(() => import("./views/campaigns/CampaignPage"));
const RunPage = lazy(() => import("./views/campaigns/RunPage"));
const TrafficProfiles = lazy(() => import("./views/TrafficProfiles"));
const SessionDetail = lazy(() => import("./views/SessionDetail"));
const SessionPair = lazy(() => import("./views/SessionPair"));
const VRFs = lazy(() => import("./views/VRFs"));
const Network = lazy(() => import("./views/Network"));
const Groups = lazy(() => import("./views/Groups"));
const Events = lazy(() => import("./views/Events"));
const Alerts = lazy(() => import("./views/Alerts"));
const Lab = lazy(() => import("./views/Lab"));
const Changes = lazy(() => import("./views/Changes"));
const RetentionHolds = lazy(() => import("./views/admin/RetentionHolds"));
const Rollouts = lazy(() => import("./views/Rollouts"));
const Users = lazy(() => import("./views/admin/Users"));
const ServerHealth = lazy(() => import("./views/admin/ServerHealth"));
const PlatformTenants = lazy(() => import("./views/admin/PlatformTenants"));
const ApprovalQueue = lazy(() => import("./views/admin/ApprovalQueue"));
const Notifications = lazy(() => import("./views/admin/Notifications"));
const Enrollment = lazy(() => import("./views/admin/Enrollment"));
const Tokens = lazy(() => import("./views/admin/Tokens"));
const Secrets = lazy(() => import("./views/admin/Secrets"));
const Maintenance = lazy(() => import("./views/admin/Maintenance"));
const AuditLog = lazy(() => import("./views/admin/AuditLog"));
const Account = lazy(() => import("./views/admin/Account"));

/** LegacyRefreshLink forwards a refresh's old address under Network devices. */
function LegacyRefreshLink() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/refreshes/${id}${search}`} replace />;
}

/** LegacyAgentLink forwards /agents/:id to the agent page of the same id. */
function LegacyAgentLink() {
  const { id } = useParams();
  return <Navigate to={`/agents/${id}`} replace />;
}

/** Group views by monitoring, managed resources, test workflows and administration. */
const NAV: { group: string; items: { to: string; label: string; platformOnly?: boolean; minRole?: "operator" | "admin" }[] }[] = [
  { group: "Monitoring", items: [
    { to: "/", label: "Overview" },
    { to: "/alerts", label: "Alerts" }, { to: "/events", label: "Events" },
    { to: "/investigate", label: "Investigate" }, { to: "/diagnostics", label: "Guided checks" },
  ] },
  { group: "Infrastructure", items: [
    { to: "/agents", label: "Shards" }, { to: "/devices", label: "Network devices" },
    { to: "/refreshes", label: "Refreshes" },
    { to: "/groups", label: "Shard groups" }, { to: "/vrfs", label: "VRFs" },
    { to: "/access", label: "Remote access" }, { to: "/changes", label: "Changes" },
  ] },
  { group: "Testing & validation", items: [
    { to: "/campaigns", label: "Realms" }, { to: "/tests", label: "Test explorer" },
    { to: "/sessions", label: "Two-ended tests" }, { to: "/traffic-profiles", label: "Traffic profiles" },
    { to: "/lab", label: "Impairments & routes" },
    { to: "/security", label: "Security testing", minRole: "operator" },
  ] },
  {
    group: "Administration",
    items: [
      { to: "/rollouts", label: "Shard upgrades", minRole: "admin" },
      { to: "/admin/users", label: "Users", minRole: "admin" },
      { to: "/admin/approvals", label: "Approval queue", minRole: "operator" },
      { to: "/admin/enrollment", label: "Enrollment", minRole: "operator" },
      { to: "/admin/tokens", label: "API tokens" },
      { to: "/admin/secrets", label: "Secrets", minRole: "operator" },
      { to: "/admin/notifications", label: "Alert destinations", minRole: "admin" },
      { to: "/admin/maintenance", label: "Maintenance" },
      { to: "/admin/holds", label: "Legal holds", minRole: "admin" },
      { to: "/admin/audit", label: "Audit log", minRole: "admin" },
      { to: "/admin/tenant", label: "Tenant settings", minRole: "admin" },
      // Cross-tenant administration, offered only to a caller who holds
      // platform standing. The server re-checks it regardless; hiding the link
      // is so the console does not advertise a page that would only 403.
      { to: "/admin/tenants", label: "Tenants", platformOnly: true, minRole: "admin" },
      { to: "/admin/health", label: "Watchtower health", platformOnly: true, minRole: "admin" },
      { to: "/admin/account", label: "Account" },
    ],
  },
];

/** The build the server is running. Public, so it needs no session. */
function ServerVersion() {
  const [v, setV] = useState<string | null>(null);
  useEffect(() => {
    fetch("/api/v1/version")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { version?: string } | null) => setV(d?.version ?? null))
      .catch(() => setV(null));
  }, []);
  if (!v) return null;
  return (
    <div className="muted" style={{ fontSize: 11 }} title="Watchtower build">
      heimdall {v}
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<Session | null>(() => loadSession());
  // An invitation link arrives with no session, and must not be intercepted by
  // the sign-in form: the person following it cannot sign in yet, which is the
  // problem the link exists to solve.
  const [inviteToken] = useState(() => new URLSearchParams(window.location.search).get("token"));
  const isInvite = window.location.pathname === "/invite" && !!inviteToken;

  // One place decides what a rejected session means, rather than every view.
  useEffect(() => {
    setAuthLostHandler(() => {
      clearSession();
      setSession(null);
    });
  }, []);

  if (isInvite && inviteToken) return <AcceptInvitation token={inviteToken} onSignedIn={setSession} />;
  if (!session) return <SignIn onSignedIn={setSession} />;
  return <WorkspaceShell key={session.tenantId} base={session} onSession={setSession} onSignOut={() => setSession(null)} />;
}

function WorkspaceShell({base, onSession, onSignOut}: {
  base: Session;
  onSession: (session: Session) => void;
  onSignOut: () => void;
}) {
  const [workspace, setWorkspace] = useState<Workspace>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setWorkspaceSession(base);
    const controller = new AbortController();
    api.get<Workspace>("/auth/workspace", controller.signal).then(data => {
      if (controller.signal.aborted) return;
      setWorkspaceSession({
        ...base, tenantId: data.tenant_id, tenantName: data.tenant_name || base.tenantName,
        email: data.support?.subject ?? base.email, role: data.role, platform: data.platform,
        labOnly: data.lab_only, canImpersonate: data.can_impersonate, featureSets: data.feature_sets, support: data.support,
      });
      setWorkspace(data);
    }).catch(e => {
      if (!controller.signal.aborted) setError(e instanceof Error ? e.message : String(e));
    });
    return () => { controller.abort(); setWorkspaceSession(null); };
  }, [base]);

  useEffect(() => {
    const support = workspace?.support;
    if (!support || support.unavailable) return;
    const timer = window.setTimeout(() => setWorkspace(current => current?.support
      ? { ...current, support: { ...current.support, unavailable: true } } : current),
      Math.max(0, Date.parse(support.expires_at) - Date.now()));
    return () => window.clearTimeout(timer);
  }, [workspace?.support]);

  async function endSupport() {
    setBusy(true);
    setError("");
    try {
      await api.del("/platform/impersonation");
      window.location.assign("/admin/users");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  if (!workspace) return error
    ? <main className="main"><p role="alert">{error}</p><button onClick={() => window.location.reload()}>Retry</button>
      <button onClick={async () => { await logout(); onSignOut(); }}>Sign out</button></main>
    : <p className="empty" role="status">Loading workspace…</p>;

  return <>
    {workspace.support && <aside className="banner warn" aria-label="Support session">
      <span>{workspace.support.unavailable ? "Support access has ended" : `Viewing as ${workspace.support.subject} · Read-only support session`}</span>{" "}
      <button disabled={busy} onClick={() => void endSupport()}>End support session</button>
      {error && <p role="alert">{error}</p>}
    </aside>}
    {!workspace.support?.unavailable && <Shell session={loadSession() ?? base} onSession={onSession} onSignOut={onSignOut}/>}
  </>;
}

/**
 * Switching tenants without signing out again. Identity spans tenants (§3.3),
 * so the same credential works in every tenant the caller holds a role in — a
 * platform admin reaches all of them, an MSP engineer the set they administer.
 */
function TenantSwitcher({ session, onSwitch }: { session: Session; onSwitch: (s: Session) => void }) {
  const [tenants, setTenants] = useState<TenantAccess[] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || tenants) return;
    listMyTenants()
      .then(setTenants)
      .catch(() => setTenants([]));
  }, [open, tenants]);

  // A token bound to one tenant cannot act in another, so offering the choice
  // would only produce a 403 the operator has to interpret.
  if (!session.tenantId) return null;

  const current = session.tenantName ?? session.tenantId.slice(0, 8);

  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} style={{ width: "100%", textAlign: "left" }}>
        <span className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Tenant
        </span>
        <br />
        {current}
        {session.role ? <span className="muted" style={{ float: "right", fontSize: 12 }}>{session.labOnly?"realm access":session.role}</span> : null}
      </button>
      {open ? (
        <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
          {tenants === null ? (
            <span className="muted" style={{ fontSize: 12 }}>Loading…</span>
          ) : tenants.length <= 1 ? (
            <span className="muted" style={{ fontSize: 12 }}>No other tenants available.</span>
          ) : (
            tenants.map((t) => (
              <button
                key={t.tenant_id}
                style={{ textAlign: "left", fontSize: 13 }}
                disabled={t.tenant_id === session.tenantId}
                onClick={() => {
                  const next = { ...session, tenantId: t.tenant_id, tenantName: t.name, role: t.role, labOnly:t.lab_only ?? false, platform: t.platform };
                  saveSession(next);
                  setOpen(false);
                  onSwitch(next);
                }}
              >
                {t.name}
                <span className="muted" style={{ float: "right", fontSize: 11 }}>{t.lab_only?"realm access":t.role}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function Shell({
  session,
  onSession,
  onSignOut,
}: {
  session: Session;
  onSession: (s: Session) => void;
  onSignOut: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const visible = (path: string) => {
    const features = routeFeatures(path);
    return (features.length === 0 || features.some(feature => session.featureSets?.includes(feature) ?? true)) &&
      (!session.support || !/^\/(access|admin\/(account|secrets|tokens|tenants|health))(\/|$)/.test(path));
  };
  const home = session.labOnly ? "/lab-scenarios" : session.featureSets?.includes("assurance") !== false ? "/"
    : session.featureSets.includes("lab_testing") ? "/campaigns"
    : session.featureSets.some(feature => feature === "device_management" || feature === "lifecycle") ? "/devices" : "/agents";
  const labNav = [{group:"Realms",items:[{to:"/lab-scenarios",label:"Realms"}]}];
  const groups = (session.labOnly ? labNav as typeof NAV : NAV).map(group => ({
    ...group, items: group.items.filter(item => visible(item.to) && (item.to !== "/" || home === "/") &&
      (!item.platformOnly || session.platform) &&
      (!item.minRole || (item.minRole === "operator" && ["operator", "admin", "owner"].includes(session.role ?? "")) ||
        (item.minRole === "admin" && ["admin", "owner"].includes(session.role ?? "")))),
  })).filter(group => group.items.length);
  const [theme, setTheme] = useState<string>(() => localStorage.getItem("heimdall.theme") ?? "");
  // The navigation drawer, on narrow screens. Closed on every navigation:
  // tapping a link and being left staring at the menu you tapped it in is the
  // most common way a mobile drawer goes wrong.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [location.pathname,session.tenantId]);
  useEffect(() => {
    document.documentElement.classList.toggle("nav-open", navOpen);
    return () => document.documentElement.classList.remove("nav-open");
  }, [navOpen]);

  useEffect(() => {
    if (theme) document.documentElement.setAttribute("data-theme", theme);
    else document.documentElement.removeAttribute("data-theme");
    localStorage.setItem("heimdall.theme", theme);
  }, [theme]);

  async function signOut() {
    await logout();
    onSignOut();
    navigate("/");
  }

  return (
    <div className="shell">
      {/* Only rendered below the breakpoint; the stylesheet hides it above. */}
      <div className="topbar">
        <button onClick={() => setNavOpen((v) => !v)} aria-label="Menu" aria-expanded={navOpen}>
          ☰
        </button>
        <img
          src="/assets/heimdall-logo.jpg"
          alt=""
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/assets/heimdall-logo.svg";
          }}
        />
        <strong>Heimdall</strong>
      </div>
      {navOpen ? (
        <button className="scrim" aria-label="Close menu" onClick={() => setNavOpen(false)} />
      ) : null}

      <nav className={navOpen ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <img
            src="/assets/heimdall-logo.jpg"
            alt=""
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/assets/heimdall-logo.svg";
            }}
          />
          <strong>Heimdall</strong>
        </div>

        {groups.map((g) => (
          <details key={g.group} open={g.group !== "Administration" || (location.pathname.startsWith("/admin/") || location.pathname === "/rollouts")}>
            <summary className="navgroup">{g.group}</summary>
            {g.items
              .map((i) => (
              <NavLink key={i.to} to={i.to} end={i.to === "/"} className={({ isActive }) => (isActive ? "navlink active" : "navlink")}>
                {i.label}
              </NavLink>
              ))}
          </details>
        ))}

        <div className="sidebar-foot">
          <ServerVersion />
          {!session.support && <TenantSwitcher session={session} onSwitch={onSession} />}
          <div title={session.tenantId}>{session.email ?? "signed in"}</div>
          <div className="row">
            <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} style={{ flex: 1 }}>
              {theme === "dark" ? "Light" : "Dark"}
            </button>
            {!session.support && <button onClick={signOut} style={{ flex: 1 }}>Sign out</button>}
          </div>
        </div>
      </nav>

      <main className="main" key={session.tenantId}>
        <RouteBoundary key={`${session.tenantId}/${location.pathname}`}><Suspense fallback={<p className="empty" role="status">Loading workspace…</p>}>
        {!visible(location.pathname) ? <p className="banner warn">This page is unavailable in the current workspace.</p>
          : location.pathname === "/" && home !== "/" && (!session.labOnly || visible(home)) ? <Navigate to={home} replace/>
          : session.labOnly?<Routes>
 <Route path="/lab-scenarios" element={<LabPortal/>}/><Route path="/lab-scenarios/:id" element={<LabPortal/>}/><Route path="/lab-scenarios/:id/runs/:run" element={<PlaybookRunPage/>}/>
 <Route path="/access/connect/:id" element={<RemoteSession/>}/><Route path="/access/session" element={<RemoteSessionTab/>}/><Route path="*" element={<Navigate to="/lab-scenarios" replace/>}/>
 </Routes>:<Routes>
 <Route path="/lab-scenarios" element={<LabPortal/>}/><Route path="/lab-scenarios/:id" element={<LabPortal/>}/><Route path="/lab-scenarios/:id/runs/:run" element={<PlaybookRunPage/>}/>
          <Route path="/" element={<Overview />} />
          <Route path="/agents" element={<Agents />} />
          <Route path="/agents/:id" element={<AgentDetail />} />
          <Route path="/agents/:id/config" element={<AgentConfig />} />
          {/* The paths these pages had when agents were called agents, and
              when there was a scorecard. Redirects rather than removals: an
              operator's bookmark and a link in an old alert email should still
              arrive somewhere. */}
          <Route path="/fleet" element={<Navigate to="/agents" replace />} />
          <Route path="/agents/:id" element={<LegacyAgentLink />} />
          <Route path="/scorecard" element={<Navigate to="/" replace />} />
          <Route path="/access" element={<Access />} />
<Route path="/access/connect/:id" element={<RemoteSession />} /><Route path="/access/session" element={<RemoteSessionTab />} />
          <Route path="/devices" element={<Devices />} />
          <Route path="/refreshes" element={<RefreshList />} />
          <Route path="/refreshes/:id" element={<RefreshWorkflow />} />
          <Route path="/refreshes/:id/devices/:device" element={<RefreshDevice />} />
          <Route path="/devices/refreshes/:id" element={<LegacyRefreshLink />} />
          <Route path="/devices/library/feeds" element={<LifecycleFeeds />} />
          <Route path="/devices/library/:kind/*" element={<HardwareEntry />} />
          <Route path="/devices/:id" element={<DeviceDetail />} />
          <Route path="/tests" element={<Tests />} />
          <Route path="/diagnostics" element={<Diagnostics />} />
          <Route path="/investigate" element={<Investigate />} />
          <Route path="/groups" element={<Groups />} />
          <Route path="/network" element={<Network />} />
          <Route path="/vrfs" element={<VRFs />} />
          <Route path="/sessions" element={<Sessions />} />
          <Route path="/traffic-profiles" element={<TrafficProfiles />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/campaigns/:id" element={<CampaignPage />} />
          <Route path="/campaign-runs/:id" element={<RunPage />} />
          <Route path="/session-pairs/:kind/:client/:responder" element={<SessionPair />} />
          <Route path="/sessions/:id" element={<SessionDetail />} />
          <Route path="/events" element={<Events />} />
          <Route path="/alerts" element={<Alerts />} />
          <Route path="/lab" element={<Lab />} />
          <Route path="/security" element={<Security />} />
          <Route path="/config" element={<Navigate to="/changes?tab=versions" replace />} />
          <Route path="/map" element={<Navigate to="/?tab=map" replace />} />
          <Route path="/changes" element={<Changes />} />
          <Route path="/admin/holds" element={<RetentionHolds />} />
          <Route path="/rollouts" element={<Rollouts />} />
          <Route path="/admin/tenants" element={<PlatformTenants />} />
          <Route path="/admin/health" element={<ServerHealth />} />
          <Route path="/admin/notifications" element={<Notifications />} />
          <Route path="/admin/approvals" element={<ApprovalQueue />} />
          <Route path="/admin/users" element={<Users />} />
          <Route path="/admin/enrollment" element={<Enrollment />} />
          <Route path="/admin/tokens" element={<Tokens />} />
          <Route path="/admin/secrets" element={<Secrets />} />
          <Route path="/admin/maintenance" element={<Maintenance />} />
          <Route path="/admin/audit" element={<AuditLog />} />
          <Route path="/admin/tenant" element={<TenantSettings />} />
          <Route path="/admin/account" element={<Account />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>}
        </Suspense></RouteBoundary>
      </main>
    </div>
  );
}
